import { type GetExamSessionQueryResponse, useGetExamSession } from '@exam-room/graphql';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useGetCookie } from '../../hooks';
import { PATHS } from '../../routes';
import { useConfig } from '../config';

type ExamSession = GetExamSessionQueryResponse['erGetExamSessionAnswer'];

interface ExamSessionProviderProps {
  children: React.ReactNode;
}

interface ExamSessionContextType {
  examSession: ExamSession;
  loading: boolean;
  error: Error | undefined;
  refetch: () => Promise<void>;
}

const ExamSessionContext = createContext<ExamSessionContextType | undefined>(undefined);

function ExamSessionProvider({ children }: Readonly<ExamSessionProviderProps>) {
  const { pathname } = useLocation();
  const { cookie } = useConfig();

  const sessionCookie = useGetCookie(cookie.examSessionCookieName);

  const {
    data: sessionData,
    loading: isLoading,
    error,
    refetch: refetchExamSession,
  } = useGetExamSession({
    fetchPolicy: 'network-only',
    skip:
      (!sessionCookie && !(import.meta.env.MODE === 'development')) ||
      !!matchPath({ path: PATHS.Exam.Break }, pathname) ||
      !!matchPath({ path: PATHS.Exam.Completed }, pathname) ||
      !!matchPath({ path: PATHS.Exam.Processing }, pathname),
  });

  const refetch = useCallback(async () => {
    await refetchExamSession();
  }, [refetchExamSession]);

  const contextValue = useMemo(
    () => ({
      examSession: sessionData?.erGetExamSessionAnswer as ExamSession, // NOTE: We cast because we'll handle the undefined case in the layout
      loading: isLoading,
      error,
      refetch,
    }),
    [sessionData, isLoading, error, refetch],
  );

  return <ExamSessionContext.Provider value={contextValue}>{children}</ExamSessionContext.Provider>;
}

function useExamSession() {
  const context = useContext(ExamSessionContext);

  if (context === undefined) {
    throw new Error('useExamSession must be used within ExamSessionProvider');
  }

  return context;
}

export { ExamSessionProvider, useExamSession, ExamSessionContext };
export type { ExamSessionContextType, ExamSession, ExamSessionProviderProps };
